import { Schema } from "effect";

export const ROAST_EVENT_NAMES = [
  "Turning point",
  "Color change",
  "First crack",
  "Second crack",
  "Heat adjustment",
  "Airflow adjustment",
  "Note",
  "Drop",
] as const;
export const LABS_ROLES = [
  "owner",
  "manager",
  "operator",
  "qc",
  "viewer",
] as const;
export type LabsRole = (typeof LABS_ROLES)[number];
export const MAX_CAPTURE_MS = 4 * 60 * 60 * 1000;
export const MAX_CHUNK_FRAMES = 120;
export const MAX_CHUNK_BYTES = 64_000;
export const MAX_MASS_MG = 10_000_000_000;
const integer = (max: number) =>
  Schema.Number.pipe(Schema.int(), Schema.between(0, max));
const label = (max: number) =>
  Schema.String.pipe(Schema.minLength(1), Schema.maxLength(max));
const celsius = Schema.NullOr(
  Schema.Number.pipe(Schema.finite(), Schema.between(-100, 600)),
);
export const CaptureFrameSchema = Schema.Struct({
  sequence: integer(30_000),
  elapsedMs: Schema.Number.pipe(
    Schema.finite(),
    Schema.between(0, MAX_CAPTURE_MS),
  ),
  receivedAt: integer(8_640_000_000_000_000),
  epoch: integer(1000),
  beanC: celsius,
  environmentC: celsius,
  quality: Schema.Literal(
    "ok",
    "missing",
    "disconnected",
    "probe_error",
    "interrupted",
  ),
});
export type CaptureFrame = typeof CaptureFrameSchema.Type;
export const CaptureEventSchema = Schema.Struct({
  id: label(100),
  name: Schema.Literal(...ROAST_EVENT_NAMES),
  elapsedMs: Schema.Number.pipe(
    Schema.finite(),
    Schema.between(0, MAX_CAPTURE_MS),
  ),
  note: Schema.String.pipe(Schema.maxLength(500)),
});
export type CaptureEvent = typeof CaptureEventSchema.Type;
export const PhidgetConfigSchema = Schema.Struct({
  serial: Schema.Number.pipe(Schema.int(), Schema.between(1, 2_147_483_647)),
  hubPort: integer(15),
  beanChannel: integer(31),
  environmentChannel: Schema.NullOr(integer(31)),
  thermocouple: Schema.Literal("K", "J", "E", "T"),
  port: Schema.Number.pipe(Schema.int(), Schema.between(1, 65535)),
});
export type PhidgetConfig = typeof PhidgetConfigSchema.Type;
export function decodePhidgetConfig(value: unknown): PhidgetConfig {
  const config = Schema.decodeUnknownSync(PhidgetConfigSchema)(value, {
    onExcessProperty: "error",
  });
  if (config.beanChannel === config.environmentChannel)
    throw new Error("Bean and environment need separate physical channels.");
  return config;
}
export const CaptureChunkSchema = Schema.Struct({
  version: Schema.Literal(1),
  frames: Schema.Array(CaptureFrameSchema).pipe(
    Schema.minItems(1),
    Schema.maxItems(MAX_CHUNK_FRAMES),
  ),
});
export function decodeCaptureChunk(json: string) {
  if (new TextEncoder().encode(json).byteLength > MAX_CHUNK_BYTES)
    throw new Error("Telemetry chunk exceeds 64 KB.");
  const chunk = Schema.decodeUnknownSync(CaptureChunkSchema)(JSON.parse(json), {
    onExcessProperty: "error",
  });
  for (let i = 0; i < chunk.frames.length; i++) {
    const frame = chunk.frames[i]!;
    const previous = chunk.frames[i - 1];
    if (
      frame.quality !== "ok" &&
      (frame.beanC !== null || frame.environmentC !== null)
    )
      throw new Error("Invalid readings must remain gaps.");
    if (
      previous &&
      (frame.sequence !== previous.sequence + 1 ||
        frame.elapsedMs <= previous.elapsedMs ||
        frame.epoch < previous.epoch)
    )
      throw new Error(
        "Telemetry sequence or clock is discontinuous within a chunk.",
      );
  }
  return chunk;
}
export function massMg(value: number, allowZero = false) {
  if (
    !Number.isSafeInteger(value) ||
    value < (allowZero ? 0 : 1) ||
    value > MAX_MASS_MG
  )
    throw new Error("Enter a valid measured mass in integer milligrams.");
  return value;
}
export function toMassMg(value: number, unit: "kg" | "g" | "lb") {
  if (!Number.isFinite(value) || value <= 0)
    throw new Error("Enter a positive measured weight.");
  return massMg(
    Math.round(
      value * (unit === "kg" ? 1_000_000 : unit === "g" ? 1000 : 453_592.37),
    ),
  );
}
export function requiredText(value: string, name: string, max = 160) {
  const result = value.trim();
  if (!result || result.length > max)
    throw new Error(`${name} must contain 1–${max} characters.`);
  return result;
}
/** Stable fingerprints reject reuse of an operation key for a different request. */
export function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  if (value && typeof value === "object")
    return `{${Object.entries(value)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`)
      .join(",")}}`;
  return JSON.stringify(value);
}
